import { useEffect, useRef, useState } from 'react'
import { Copy, ShareNetwork } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

type ReferralActionsProps = {
  code: string
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const input = document.createElement('textarea')
  input.value = text
  input.setAttribute('readonly', '')
  input.style.position = 'fixed'
  input.style.opacity = '0'
  document.body.appendChild(input)
  input.select()
  const copied = document.execCommand('copy')
  input.remove()
  if (!copied) throw new Error('copy_failed')
}

export function ReferralActions({ code }: ReferralActionsProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<'copied' | 'failed' | null>(null)
  const timer = useRef<number | null>(null)
  const available = Boolean(code)

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    [],
  )

  const announce = (next: 'copied' | 'failed') => {
    setStatus(next)
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setStatus(null), 2200)
  }

  const copy = async () => {
    if (!available) return
    try {
      await copyText(code)
      announce('copied')
    } catch {
      announce('failed')
    }
  }

  const share = async () => {
    if (!available) return
    const text = t('referral.shareText', { code })
    try {
      if (navigator.share) {
        await navigator.share({ text })
      } else {
        await copyText(text)
        announce('copied')
      }
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return
      announce('failed')
    }
  }

  return (
    <div className="referral-actions">
      <button type="button" onClick={() => void copy()} disabled={!available}>
        <Copy weight="bold" aria-hidden />
        {t('referral.copy')}
      </button>
      <button type="button" onClick={() => void share()} disabled={!available}>
        <ShareNetwork weight="bold" aria-hidden />
        {t('referral.share')}
      </button>
      <span className="referral-actions__status" role="status" aria-live="polite">
        {status === 'copied'
          ? t('referral.copied')
          : status === 'failed'
            ? t('referral.copyFailed')
            : ''}
      </span>
    </div>
  )
}
